import {Injectable} from '@angular/core';
import {map} from 'rxjs/operators';
import {DataContextService} from '../../datacontext.service';
import {BehaviorSubject, Observable, throwError} from 'rxjs';
import {
  BulkInput,
  EditReservationDetails,
  ReservationDetails,
  ReservationItemsInput,
  ReservationItemsResponse,
} from '../../../models/meetings.model';
import {DashboardFeature} from 'src/app/models/dashboard-common.model';
import {ToastService} from 'src/app/services/toast.service';
import {CreateReservationResponse, SpaceBookingInput} from 'src/app/models/find-a-space.model';
import {BookingDetails} from '../../../models/bookings.model';
import {SpinnerService} from '../../../common/spinner/spinner.service';

@Injectable({
  providedIn: 'root',
})
export class ReservationsService {

  constructor(private dataContextService: DataContextService, private toasterService: ToastService, private spinnerService: SpinnerService) {
  }

  reservationId: number;
  reservationName: string;
  private reservationDetails = new BehaviorSubject<ReservationDetails>(null);
  reservationDetails$ = this.reservationDetails.asObservable();

  getReservation(id: number, flags: string = '', showSpinner = true, showError = true): Observable<ReservationDetails> {
    let url = `Reservations/${id}`;
    if (flags) {
      url += `?reservationFeatureFlags=${flags}`;
    }
    return this.dataContextService.getData(url, showSpinner).pipe(
      map(res => {
        if (res.status === 200) {
          const response = res.data as ReservationDetails;
          if (response.owner) {
            response.owner.firstName = this.dataContextService.getUserFirstName(response.owner);
            response.owner.lastName = response.owner.lastName ? response.owner.lastName : '';
          }
          if (response.pointOfContact) {
            response.pointOfContact.firstName = this.dataContextService.getUserFirstName(response.pointOfContact);
            response.pointOfContact.lastName = response.pointOfContact.lastName ? response.pointOfContact.lastName : '';
          }
          if (response.specialist) {
            response.specialist.firstName = this.dataContextService.getUserFirstName(response.specialist);
            response.specialist.lastName = response.specialist.lastName ? response.specialist.lastName : '';
          }
          return response;
        } else {
          if (showError)
            return this.dataContextService.showErrorToaster(res);
          else
            return null;
        }
      })
    );
  }

  getReservationItems(reservationId: number, input?: ReservationItemsInput): Observable<ReservationItemsResponse> {
    const url = `Reservation/${reservationId}/ReservationItems`;
    return this.dataContextService.postData(url, input).pipe(
      map((res) => {
        if (res.status === 200) {
          const response = res.data as ReservationItemsResponse;
          response.items.forEach(i => {
            if (i.virtualBooking) {
              if (i.virtualBooking.producer) {
                i.virtualBooking.producer.firstName = this.dataContextService.getUserFirstName(i.virtualBooking.producer),
                  i.virtualBooking.producer.lastName = i.virtualBooking.producer.lastName ? i.virtualBooking.producer.lastName : ''
              }
              if (i.virtualBooking.facilitator) {
                i.virtualBooking.facilitator.firstName = this.dataContextService.getUserFirstName(i.virtualBooking.facilitator),
                  i.virtualBooking.facilitator.lastName = i.virtualBooking.facilitator.lastName ? i.virtualBooking.facilitator.lastName : ''
              }
            }
            if (i.spaceBooking) {
              if (i.spaceBooking.specialist) {
                i.spaceBooking.specialist.firstName = this.dataContextService.getUserFirstName(i.spaceBooking.specialist),
                  i.spaceBooking.specialist.lastName = i.spaceBooking.specialist.lastName ? i.spaceBooking.specialist.lastName : ''
              }
            }
          })
          if (response.reservationItemFacets && response.reservationItemFacets.spaceBookingSpecialists) {
            response.reservationItemFacets.spaceBookingSpecialists.forEach((i) => {
              i.firstName = this.dataContextService.getUserFirstName(i),
                i.lastName = i.lastName ? i.lastName : ''
            })
          }
          return response;
        } else {
          return this.dataContextService.showErrorToaster(res);
        }
      })
    );
  }

  updateReservationSpecialist(reservationId: number, specialistId: number, showGlobalSpinner: boolean = false): Observable<any> {
    const url = `Reservations/${reservationId}/Specialist`;
    return this.dataContextService.patchData(url, { specialistId }, showGlobalSpinner, false)
      .pipe(
        map(res => {
          if (res.status === 200) {
            return 'OK';
          } else {
            this.dataContextService.showErrorToaster(res);
          }
        })
      );
  }

  getBulkReservationItems(reservationId: number, input: ReservationItemsInput): Observable<ReservationItemsResponse> {
    input.page = 0;
    input.limit = 500;
    input.features = DashboardFeature[DashboardFeature.IncludeFacets];
    const url = `Reservation/${reservationId}/ReservationItems`;
    return this.dataContextService.postData(url, input, true)
      .pipe(map((res) => {
        if (res.status === 200) {
          const response = res.data as ReservationItemsResponse;
          response.items.forEach(i => {
            if (i.virtualBooking) {
              if (i.virtualBooking.producer) {
                i.virtualBooking.producer.firstName = this.dataContextService.getUserFirstName(i.virtualBooking.producer),
                  i.virtualBooking.producer.lastName = i.virtualBooking.producer.lastName ? i.virtualBooking.producer.lastName : ''
              }
              if (i.virtualBooking.facilitator) {
                i.virtualBooking.facilitator.firstName = this.dataContextService.getUserFirstName(i.virtualBooking.facilitator),
                  i.virtualBooking.facilitator.lastName = i.virtualBooking.facilitator.lastName ? i.virtualBooking.facilitator.lastName : ''
              }
            }
            if (i.spaceBooking) {
              if (i.spaceBooking.specialist) {
                i.spaceBooking.specialist.firstName = this.dataContextService.getUserFirstName(i.spaceBooking.specialist),
                  i.spaceBooking.specialist.lastName = i.spaceBooking.specialist.lastName ? i.spaceBooking.specialist.lastName : ''
              }
            }
          })
          if (response.reservationItemFacets && response.reservationItemFacets.spaceBookingSpecialists) {
            response.reservationItemFacets.spaceBookingSpecialists.forEach((i) => {
              i.firstName = this.dataContextService.getUserFirstName(i),
                i.lastName = i.lastName ? i.lastName : ''
            })
          }
          return response;
        }
        else {
          return this.dataContextService.showErrorToaster(res);
        }
      }));
  }

  //copyBulkReservation(userId: number, reservationId: number, copyBulkBooking: CopyBulkBooking, isConciergeAllocation: boolean = true) {
  //  const url = `Users/${userId}/Reservations/${reservationId}/Copy`;
  //  const isDelegate = userId != DataContextService.LoggedInUserId && isConciergeAllocation;

  //  return this.dataContextService.patchData(url, copyBulkBooking, true, true).pipe(map((res) => {
  //    if (res.status == 200) {
  //      const id = res.data.id;
  //      const revision = res.data.revision;
  //      const response: IdWithRevision = { id, revision };
  //      return response;
  //    } else {
  //      if (res.status == 428) {
  //        const spaceNames = [];
  //        if (res.data.spaces) {
  //          if (res.data.message) {
  //            res.data.message = res.data.message.replace('.', '');
  //          }
  //          res.data.spaces.forEach(element => {
  //            spaceNames.push(element.spaceName);
  //          });
  //          res.data.message = res.data.message + ': ' + spaceNames.toString();
  //        }
  //      }
  //      return this.toasterService.openError(
  //        res.data ? res.data.message : res.statusText || this.dataContextService.translateText('Translation.Error')
  //      );
  //    }
  //  }));
  //}


  copyBooking(userId: number, reservationId: number, data: BulkInput) {
    const url = `Users/${userId}/Reservations/${reservationId}/Copy`;
    return this.dataContextService.patchData(url, data, true)
      .pipe(map((res) => {
        if (res.status == 200) {
          return res.data;
        } else {
          return this.toasterService.openError(
            res.data ? res.data.message : res.statusText || this.dataContextService.translateText('Translation.Error')
          );
        }
      }));
  }


  moveBooking(userId: number, reservationId: number, data: BulkInput) {
    const url = `Users/${userId}/Reservations/${reservationId}/Move`;
    return this.dataContextService.patchData(url, data, true).pipe(map((res) => {
      if (res.status == 200) {
        return res.data;
      } else {
        return this.toasterService.openError(
          res.data ? res.data.message : res.statusText || this.dataContextService.translateText('Translation.Error')
        );
      }
    }));
  }


  createReservation(id: number, data: any, flags?: string): Observable<CreateReservationResponse> {
    let url = `Users/${id}/Reservations`;
    if (flags) {
      url += `?reservationFeatureFlags=${flags}`;
    }
    return this.dataContextService.postData(url, data, true, false, true).pipe(
      map(res => {
        if (res.status === 200) {
          return res.data as CreateReservationResponse;
        } else {
          throw throwError(res);
        }
      })
    );
  }

  editReservation(reservationId: number, editReservationInput: EditReservationDetails, flags: string): Observable<ReservationDetails> {
    const url = `Reservations/${reservationId}?reservationFeatureFlags=${flags}`;
    return this.dataContextService.putData(url, editReservationInput, true).pipe(map((res: any) => {
      if (res.status === 200) {
        const reservationDetails = res.data as ReservationDetails;
        this.reservationDetails.next(reservationDetails);
        return res;
      } else {
        this.toasterService.openError(
          res.data ? res.data.message : res.statusText || this.dataContextService.translateText('Translation.Error')
        );
        throw throwError(res);
      }
    }));
  }

  addSpaceBooking(reservationId: number, spaceBookingInput: SpaceBookingInput) {
    const url = `Reservations/${reservationId}/SpaceBookings`;
    return this.dataContextService.postData(url, spaceBookingInput, true, true).pipe(map(res => {
      this.spinnerService.stop();
      if (res.status == 200) {
        return res.data as BookingDetails;
      } else {
        this.dataContextService.showErrorToaster(res);
        return null;
      }
    }));
  }

  checkForDisableEndLTR(reservationDetails: ReservationDetails): boolean {
    if (reservationDetails.owner.isActive == false)
      return true;
    else if (this.dataContextService.checkCancelledStatus(reservationDetails.status))
      return true;
    else if (reservationDetails.stopSeriesDate && new Date(reservationDetails.stopSeriesDate) < new Date())
      return true;
    else
      return false;
  }
}
